/**
 * Make the advertised bundle price equal the price Shopify actually charges, in
 * every currency -- without disturbing the USD price points.
 *
 * Shopify converts a FIXED discount at the raw FX rate but CEILING-ROUNDS the product
 * price to a whole unit: 59.99*1.4347 = 86.07 renders as $87.00, while 9.99*1.4347
 * stays $14.33. Liquid only sees the rounded price, so a percentage back-solved from
 * it drifts by up to ~17c (median 2c) -- always with the customer paying more than we
 * showed. JS has the true rate, so it can reproduce Shopify's own arithmetic.
 *
 * In the base currency rate === 1, the correction is exactly zero and nothing is
 * touched -- $109.99 / $149.99 are safe by construction, not by luck.
 *
 * Fails open: any missing datum, unparseable probe, or implausible result leaves the
 * server-rendered price exactly as it is.
 */
(function () {
  'use strict';

  // Several sections include this asset and two can share a page (collection hero +
  // courses ladder), so a duplicate <script src> would run it twice.
  if (typeof window !== 'undefined' && window.__saifPriceExactLoaded) return;
  if (typeof window !== 'undefined') window.__saifPriceExactLoaded = true;

  /**
   * Learn Shopify's money format from one known rendered amount instead of
   * reimplementing it for 107 currencies. The probe is always 1234567.89, so:
   *   - digit count gives the decimal places (7 digits -> 0dp, 9 -> 2dp)
   *   - the separators reveal themselves by position
   * Handles "¥1,234,568", "Rp 1.234.567,89", "1 234 567,89 zl", "1.234.568 kr".
   */
  function parseMoneyFormat(probe) {
    if (!probe) return null;
    var digits = probe.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 10) return null;   // not our probe
    var decimals = digits.length - 7;                            // 1234567 = 7 digits
    if (decimals < 0 || decimals > 3) return null;

    var first = probe.search(/\d/);
    var last = probe.length - 1 - probe.split('').reverse().join('').search(/\d/);
    if (first < 0 || last < first) return null;

    var body = probe.slice(first, last + 1);
    var seps = body.match(/\D+/g) || [];
    var group = '', decimal = '';
    if (decimals > 0 && seps.length) {
      decimal = seps[seps.length - 1];
      group = seps.length > 1 ? seps[0] : '';
    } else if (seps.length) {
      group = seps[0];
    }
    // A decimal separator that also groups would make the format ambiguous.
    if (decimals > 0 && group && group === decimal) return null;

    return {
      prefix: probe.slice(0, first),
      suffix: probe.slice(last + 1),
      group: group,
      decimal: decimal,
      decimals: decimals
    };
  }

  /** Render presentment cents through the learned format. */
  function formatMoney(cents, f) {
    var s = (cents / 100).toFixed(f.decimals);
    var parts = s.split('.');
    var int = parts[0];
    var neg = int.charAt(0) === '-';
    if (neg) int = int.slice(1);
    if (f.group) int = int.replace(/\B(?=(\d{3})+(?!\d))/g, f.group);
    return f.prefix + (neg ? '-' : '') + int +
           (f.decimals > 0 ? f.decimal + parts[1] : '') + f.suffix;
  }

  /**
   * ISO 4217 minor units. This governs how Shopify ROUNDS a converted discount, and
   * it is NOT the same as how the currency is DISPLAYED -- Hungarian forint renders
   * as "19.200 Ft" with no decimals yet is charged to two, because ISO gives HUF an
   * exponent of 2. Yen really is exponent 0 and is charged to whole yen. Getting this
   * wrong is what made an early version miss on HUF and MNT; verified against 94 live
   * carts, this table reproduces checkout exactly in all of them.
   * Everything not listed is the default, 2.
   */
  var ISO_ZERO = ['BIF','CLP','DJF','GNF','ISK','JPY','KMF','KRW','PYG','RWF',
                  'UGX','UYI','VND','VUV','XAF','XOF','XPF'];
  var ISO_THREE = ['BHD','IQD','JOD','KWD','LYD','OMR','TND'];

  function isoDecimals(code) {
    if (!code) return 2;
    code = String(code).toUpperCase();
    for (var i = 0; i < ISO_ZERO.length; i++) if (ISO_ZERO[i] === code) return 0;
    for (var j = 0; j < ISO_THREE.length; j++) if (ISO_THREE[j] === code) return 3;
    return 2;
  }

  /**
   * Shopify rounds a converted discount to the currency's ISO precision:
   * 9.99*165.4032 = 1652.378 yen is charged as 1652 yen. cart.js always speaks in
   * 1/100 units, so a 0-decimal currency rounds in steps of 100.
   */
  function roundToCurrency(cents, decimals) {
    var step = Math.pow(10, 2 - decimals);
    return Math.round(cents / step) * step;
  }

  function computeExact(list, baseDisc, rate, decimals) {
    var disc = roundToCurrency(baseDisc * rate, decimals);
    return { price: list - disc, disc: disc };
  }

  // ---- exported for the self-test harness; harmless in the browser -------------
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseMoneyFormat: parseMoneyFormat, formatMoney: formatMoney,
                       roundToCurrency: roundToCurrency, computeExact: computeExact,
                       isoDecimals: isoDecimals, bundleValues: bundleValues };
    return;
  }

  /**
   * Every bundle figure on the site, derived from one context. The whole site prices
   * the SAME product with the SAME two automatic discounts, so a single named set
   * keeps the courses ladder, the cart upsell, the collection hero and the course
   * detail page from each re-deriving (and re-breaking) this arithmetic.
   *   unit/reg   one course's price / compare-at, in presentment cents
   *   priceN     what checkout actually charges for N courses
   *   perN       priceN per course
   *   discN      the discount itself (list - price)
   *   saveNreg   saving against the REGULAR (compare-at) total, not the list total
   *   d1to2/d2to3  the "+$X to add another" deltas
   */
  function bundleValues(unit, reg, d2, d3, rate, mathDp) {
    var list2 = unit * 2, list3 = unit * 3;
    var p2 = computeExact(list2, d2, rate, mathDp).price;
    var p3 = computeExact(list3, d3, rate, mathDp).price;
    return {
      unit: unit, reg: reg,
      price2: p2, price3: p3,
      per2: Math.round(p2 / 2), per3: Math.round(p3 / 3),
      disc2: list2 - p2, disc3: list3 - p3,
      save2reg: reg ? reg * 2 - p2 : 0,
      save3reg: reg ? reg * 3 - p3 : 0,
      d1to2: p2 - unit, d2to3: p3 - p2
    };
  }

  function apply() {
    var probeEl = document.querySelector('[data-saif-money-probe]');
    var fmt = probeEl && parseMoneyFormat(probeEl.getAttribute('data-saif-money-probe'));
    if (!fmt) return;                                   // unknown format -> touch nothing

    var cur = window.Shopify && window.Shopify.currency;
    var rate = parseFloat((cur && cur.rate) || '1');
    if (!(rate > 0)) return;

    // Two different precisions, deliberately. The DISCOUNT is rounded to the
    // currency's ISO minor units (how Shopify charges); the RESULT is rendered with
    // the probe's decimals (how Shopify displays). HUF is charged to 2dp but shown
    // with 0 -- conflating the two is a wrong price on screen.
    var mathDp = isoDecimals(cur && cur.active);

    // Published so sections that build money strings in their own script (the courses
    // picker's TIER map) can use the same arithmetic instead of a stale percentage.
    window.SaifPrice = {
      rate: rate,
      money: function (cents) { return formatMoney(cents, fmt); },
      exact: function (list, baseDisc) { return computeExact(list, baseDisc, rate, mathDp).price; }
    };

    // ---- named bundle figures ------------------------------------------------
    var ctx = document.querySelector('[data-saif-bundle]');
    if (ctx) {
      var unit = parseInt(ctx.getAttribute('data-unit'), 10);
      var reg = parseInt(ctx.getAttribute('data-reg'), 10) || 0;
      var d2 = parseInt(ctx.getAttribute('data-d2'), 10);
      var d3 = parseInt(ctx.getAttribute('data-d3'), 10);
      if (unit > 0 && d2 > 0 && d3 > 0) {
        var v = bundleValues(unit, reg, d2, d3, rate, mathDp);
        window.SaifPrice.values = v;
        var named = document.querySelectorAll('[data-saif-money]');
        for (var k = 0; k < named.length; k++) {
          var key = named[k].getAttribute('data-saif-money');
          var val = v[key];
          // Only ever replace with a sane positive figure; otherwise keep Liquid's.
          if (typeof val === 'number' && isFinite(val) && val > 0) {
            named[k].textContent = formatMoney(val, fmt);
          }
        }
        if (typeof window.SaifPriceReady === 'function') window.SaifPriceReady(v);
      }
    }

    var nodes = document.querySelectorAll('[data-saif-price]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var list = parseInt(el.getAttribute('data-list'), 10);
      var reg = parseInt(el.getAttribute('data-reg'), 10);
      var base = parseInt(el.getAttribute('data-base-disc'), 10);
      var cc = parseInt(el.getAttribute('data-cc'), 10);
      var exact = parseInt(el.getAttribute('data-exact'), 10);

      /* SAIF_PRICE_EXACT_V2 (2026-08-02) — an authoritative price wins outright.
         data-exact carries a real bundle PRODUCT's price, already converted by Liquid
         into the visitor's currency, so there is nothing for this script to correct;
         recomputing it from the old base-discount put $149.99 back over the $150.00 the
         page had already read off the product. Still refresh the save/percent/per-course
         figures below, since those are derived from whichever price ends up rendered. */
      if (exact > 0) {
        el.textContent = formatMoney(exact, fmt);
        var xcard = el.closest('.saif-pricing__card') || document;
        var xsave = xcard.querySelector('[data-saif-save]');
        if (xsave && reg > exact) xsave.textContent = formatMoney(reg - exact, fmt);
        var xpct = xcard.querySelector('[data-saif-pct]');
        if (xpct && reg > 0 && exact < reg) xpct.textContent = String(Math.round((reg - exact) * 100 / reg));
        var xper = xcard.querySelector('[data-saif-per]');
        if (xper && cc > 0) xper.textContent = formatMoney(Math.round(exact / cc), fmt);
        continue;
      }

      if (!list || !base || !cc) continue;              // nothing to correct

      var out = computeExact(list, base, rate, mathDp);
      // Sanity: a discount must be positive and smaller than the list price. If the
      // numbers ever stop making sense, leave the rendered price alone.
      if (!(out.price > 0 && out.price < list)) continue;

      el.textContent = formatMoney(out.price, fmt);

      var card = el.closest('.saif-pricing__card') || document;
      var saveEl = card.querySelector('[data-saif-save]');
      if (saveEl && reg > out.price) saveEl.textContent = formatMoney(reg - out.price, fmt);

      var pctEl = card.querySelector('[data-saif-pct]');
      if (pctEl && reg > 0 && out.price < reg) {
        pctEl.textContent = String(Math.round((reg - out.price) * 100 / reg));
      }

      var perEl = card.querySelector('[data-saif-per]');
      if (perEl) perEl.textContent = formatMoney(Math.round(out.price / cc), fmt);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
})();
